import { useLanguage } from '../../../../contexts/LanguageContext';
import toastService from '../../../../utils/toastService';
import { formatErrorMessage } from '../../../../utils/errorUtils';

/**
 * Rapor detayındaki 3 alt liste sekmesi (Maliyet/Katılımcı/Eğitmen) aynı add+delete+list şeklini
 * paylaşıyordu — bu ortak factory'e indirgendi. `useTrainingReportCostsData.js`/
 * `useTrainingReportParticipantsData.js`/`useTrainingReportInstructorsData.js` artık bunun ince
 * sarmalayıcıları (kendi alan adlarını — `costsApi`/`addCost` vb. — döndürmeye devam ediyorlar,
 * ilgili Tab bileşenleri değişmedi). Bkz. docs/LMS-Egitim-Raporlari-API.md §4-6.
 *
 * `onAddError(err, th)` opsiyonel — yalnızca eğitmen sekmesinin 409 (zaten atanmış) özel mesajı
 * için kullanılıyor; verilmezse genel `addError` mesajı gösterilir.
 */
const useTrainingReportSubListData = ({ trainingReportId, useGetQuery, useAddMutation, useDeleteMutation, i18nNamespace, onAddError }) => {
    const { th } = useLanguage();

    const {
        data: itemsApi,
        isLoading,
        isFetching,
        refetch
    } = useGetQuery({ trainingReportId, pageNumber: 1, pageSize: 100 }, { skip: !trainingReportId, refetchOnMountOrArgChange: true });

    const [addMutation, { isLoading: isAdding }] = useAddMutation();
    const [deleteMutation, { isLoading: isDeleting }] = useDeleteMutation();

    const addItem = async (payload) => {
        try {
            await addMutation({ trainingReportId, ...payload }).unwrap();
            toastService.success(th(`trainingReport.${i18nNamespace}.addSuccess`), th('common.success'));
            refetch();
            return true;
        } catch (err) {
            const message = onAddError ? onAddError(err, th) : formatErrorMessage(err) || th(`trainingReport.${i18nNamespace}.addError`);
            toastService.error(message, th('common.error'));
            return false;
        }
    };

    const removeItem = async (id) => {
        try {
            await deleteMutation(id).unwrap();
            toastService.success(th(`trainingReport.${i18nNamespace}.deleteSuccess`), th('common.success'));
            refetch();
            return true;
        } catch (err) {
            toastService.error(formatErrorMessage(err) || th(`trainingReport.${i18nNamespace}.deleteError`), th('common.error'));
            return false;
        }
    };

    return {
        itemsApi: itemsApi || { items: [], totalCount: 0 },
        isLoading,
        isFetching,
        isAdding,
        isDeleting,
        addItem,
        removeItem,
        refetch
    };
};

export default useTrainingReportSubListData;
